import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';

import { createBlog, updateBlog } from '../utils/database';
import { updateSitemap } from '../utils/sitemapUpdater';
import ImageUpload from '../components/BlogCreation/ImageUpload';
import FAQBlock from '../components/BlogCreation/FAQBlock';
import BlogPreview from '../components/BlogCreation/BlogPreview';
import RichTextEditor from '../components/BlogCreation/RichTextEditor';
import RichTextToolbar from '../components/BlogCreation/RichTextToolbar';

const BlogCreation = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  // Check if we're editing an existing blog
  const editBlog = location.state?.editBlog || null;
  const isEditing = !!(editBlog && editBlog.id);

  // Enhanced blog form state
  const [blogData, setBlogData] = useState({
    // Basic Info
    title: '',
    slug: '',
    excerpt: '',
    author: currentUser?.email || '',
    tags: '',

    // SEO Fields
    metaTitle: '',
    metaDescription: '',
    primaryKeyword: '',
    secondaryKeywords: '',
    location: '',

    // Job Details
    jobType: '',
    projectStartDate: '',
    projectEndDate: '',
    clientType: 'Residential', // Default to Residential

    // Images
    featuredImage: null,
    beforeImage: null,
    afterImage: null,
    gallery: [],

    // Content Blocks
    introParagraph: '',
    beforeSection: '',
    beforeProcessSections: [], // New flexible sections before process
    processSections: [], // New dynamic process sections
    afterProcessSections: [], // New flexible sections after process
    afterSection: '',
    clientTestimonial: '',
    callToActionText: '',
    callToActionLink: '',

    // Rich Content Section
    richContentSections: [], // New rich text content sections with styling

    // Additional
    faqs: [],
    internalLinks: '',

    // Publishing
    published: false
  });

  // Pre-fill form data if editing
  useEffect(() => {
    if (editBlog && editBlog.id) {
      setFormLoading(true);
      console.log('Loading blog data for editing:', editBlog);
      console.log('Process sections from database:', editBlog.processSections);
      console.log(
        'Before process sections from database:',
        editBlog.beforeProcessSections
      );
      console.log(
        'After process sections from database:',
        editBlog.afterProcessSections
      );
      setBlogData({
        // Basic Info
        title: editBlog.title || '',
        slug: editBlog.slug || '',
        excerpt: editBlog.excerpt || '',
        author: editBlog.author || currentUser?.email || '',
        tags: Array.isArray(editBlog.tags)
          ? editBlog.tags.join(', ')
          : editBlog.tags || '',

        // SEO Fields
        metaTitle: editBlog.metaTitle || '',
        metaDescription: editBlog.metaDescription || '',
        primaryKeyword: editBlog.primaryKeyword || '',
        secondaryKeywords: Array.isArray(editBlog.secondaryKeywords)
          ? editBlog.secondaryKeywords.join(', ')
          : editBlog.secondaryKeywords || '',
        location: editBlog.location || '',

        // Job Details
        jobType: editBlog.jobType || '',
        projectStartDate: editBlog.projectStartDate
          ? new Date(editBlog.projectStartDate).toISOString().split('T')[0]
          : '',
        projectEndDate: editBlog.projectEndDate
          ? new Date(editBlog.projectEndDate).toISOString().split('T')[0]
          : '',
        clientType: editBlog.clientType || '',

        // Images
        featuredImage: editBlog.featuredImage || null,
        beforeImage: editBlog.beforeImage || null,
        afterImage: editBlog.afterImage || null,
        gallery: editBlog.gallery || [],

        // Content Blocks
        introParagraph: editBlog.introParagraph || '',
        beforeSection: editBlog.beforeSection || '',
        beforeProcessSections:
          editBlog.beforeProcessSections &&
          editBlog.beforeProcessSections.length > 0
            ? editBlog.beforeProcessSections
            : [],
        processSections:
          editBlog.processSections && editBlog.processSections.length > 0
            ? editBlog.processSections
            : editBlog.processSection
            ? [
                {
                  id: Date.now(),
                  title: 'Project Process',
                  description: editBlog.processSection || '',
                  images: []
                }
              ]
            : [],
        afterProcessSections:
          editBlog.afterProcessSections &&
          editBlog.afterProcessSections.length > 0
            ? editBlog.afterProcessSections
            : [],
        afterSection: editBlog.afterSection || '',
        clientTestimonial: editBlog.clientTestimonial || '',
        callToActionText: editBlog.callToActionText || '',
        callToActionLink: editBlog.callToActionLink || '',

        // Rich Content Sections
        richContentSections:
          editBlog.richContentSections &&
          editBlog.richContentSections.length > 0
            ? editBlog.richContentSections
            : [],

        // Additional
        faqs: editBlog.faqs || [],
        internalLinks: editBlog.internalLinks || '',

        // Publishing
        published: editBlog.published || false
      });
      console.log(
        'Process sections after loading:',
        editBlog.processSections && editBlog.processSections.length > 0
          ? editBlog.processSections
          : editBlog.processSection
          ? [
              {
                id: Date.now(),
                title: 'Project Process',
                description: editBlog.processSection || '',
                images: []
              }
            ]
          : []
      );
      setFormLoading(false);
    }
  }, [editBlog, currentUser?.email]);

  // Debug: Log current process sections state
  useEffect(() => {
    if (isEditing) {
      console.log(
        'Current blogData.processSections:',
        blogData.processSections
      );
      console.log(
        'Current blogData.beforeProcessSections:',
        blogData.beforeProcessSections
      );
      console.log(
        'Current blogData.afterProcessSections:',
        blogData.afterProcessSections
      );
    }
  }, [
    blogData.processSections,
    blogData.beforeProcessSections,
    blogData.afterProcessSections,
    isEditing
  ]);

  // Utah cities and counties for location dropdown
  const utahCities = [
    // Box Elder County
    'Tremonton',
    'Brigham City',
    'Garland',
    'Bear River City',
    'Corinne',
    'Deweyville',
    'Honeyville',
    'Elwood',
    'Fielding',
    'Bothwell',
    'Collinston',
    'Riverside',
    'Plymouth',
    'Portage',
    'Willard',
    'Mantua',
    'Perry',
    'Snowville',
    'Howell',
    'Thatcher',
    'Penrose',
    'South Willard',
    'Beaver Dam',
    'Park Valley',
    'Grouse Creek',
    'Yost',
    'Rosette',
    'Promontory',
    'Blue Creek',
    // Cache County
    'Logan',
    'Smithfield',
    'Hyrum',
    'Mendon',
    'Wellsville',
    'Providence',
    'Hyde Park',
    'North Logan',
    'Nibley',
    'Clarkston',
    'Newton',
    'Lewiston',
    'Millville',
    'Paradise',
    'Trenton',
    'Richmond',
    'Cache Junction',
    'Amalga',
    'Benson',
    'Cornish',
    'Cove',
    'Petersboro',
    'Avon',
    'College Ward',
    'Young Ward',
    'River Heights',
    // Weber County
    'Ogden',
    'Roy',
    'Plain City',
    'Farr West',
    'Harrisville',
    'North Ogden',
    'Pleasant View',
    'West Haven',
    'Hooper',
    'Marriott-Slaterville',
    'South Ogden',
    'Riverdale',
    'Uintah',
    'Washington Terrace',
    'South Weber',
    // Davis County
    'Layton',
    'Kaysville',
    'Syracuse',
    'Clearfield',
    'West Point',
    'Clinton',
    'Sunset',
    'Farmington',
    'Fruit Heights',
    // Rich County
    'Garden City',
    'Laketown',
    'Randolph',
    'Woodruff'
  ];

  // Utah counties available for future use
  // const utahCounties = [
  //   'Box Elder', 'Cache', 'Weber', 'Davis', 'Salt Lake', 'Utah',
  //   'Tooele', 'Summit', 'Wasatch', 'Morgan', 'Rich', 'Duchesne',
  //   'Uintah', 'Daggett', 'Carbon', 'Emery', 'Grand', 'San Juan',
  //   'Kane', 'Garfield', 'Iron', 'Washington', 'Beaver', 'Millard',
  //   'Sevier', 'Piute', 'Wayne', 'Sanpete', 'Juab'
  // ];

  const jobTypes = [
    'Kitchen Remodel',
    'Bathroom Remodel',
    'Custom Cabinets',
    'Whole Home',
    'Basement Remodel',
    'Deck & Patio',
    'Other'
  ];

  const authorOptions = [
    'Rodney',
    'Nate',
    'Chance',
    'Jaden',
    'Oakandstoneremodel@gmail.com'
  ];

  const clientTypes = ['Residential', 'Commercial', 'Luxury'];

  // Auto-generate slug from title
  useEffect(() => {
    if (blogData.title && !blogData.slug) {
      const slug = blogData.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      setBlogData((prev) => ({ ...prev, slug }));
    }
  }, [blogData.title, blogData.slug]);

  // Auto-generate excerpt from content
  useEffect(() => {
    if (blogData.introParagraph && !blogData.excerpt) {
      const excerpt =
        blogData.introParagraph.length > 150
          ? blogData.introParagraph.substring(0, 150) + '...'
          : blogData.introParagraph;
      setBlogData((prev) => ({ ...prev, excerpt }));
    }
  }, [blogData.introParagraph, blogData.excerpt]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBlogData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (field, image) => {
    setBlogData((prev) => ({
      ...prev,
      [field]: image
    }));
  };

  const handleGalleryChange = (images) => {
    setBlogData((prev) => ({
      ...prev,
      gallery: images
    }));
  };

  const handleFaqsChange = (faqs) => {
    setBlogData((prev) => ({
      ...prev,
      faqs
    }));
  };

  const addProcessSection = () => {
    setBlogData((prev) => ({
      ...prev,
      processSections: [
        ...prev.processSections,
        {
          id: Date.now(),
          title: '',
          description: '',
          images: []
        }
      ]
    }));
  };

  const removeProcessSection = (index) => {
    setBlogData((prev) => ({
      ...prev,
      processSections: prev.processSections.filter((_, i) => i !== index)
    }));
  };

  const updateProcessSection = (index, field, value) => {
    setBlogData((prev) => ({
      ...prev,
      processSections: prev.processSections.map((section, i) =>
        i === index ? { ...section, [field]: value } : section
      )
    }));
  };

  const updateProcessSectionImages = (index, images) => {
    setBlogData((prev) => ({
      ...prev,
      processSections: prev.processSections.map((section, i) =>
        i === index ? { ...section, images } : section
      )
    }));
  };

  // Functions for before process sections
  const addBeforeProcessSection = () => {
    setBlogData((prev) => ({
      ...prev,
      beforeProcessSections: [
        ...prev.beforeProcessSections,
        {
          id: Date.now(),
          title: '',
          description: '',
          images: []
        }
      ]
    }));
  };

  const removeBeforeProcessSection = (index) => {
    setBlogData((prev) => ({
      ...prev,
      beforeProcessSections: prev.beforeProcessSections.filter(
        (_, i) => i !== index
      )
    }));
  };

  const updateBeforeProcessSection = (index, field, value) => {
    setBlogData((prev) => ({
      ...prev,
      beforeProcessSections: prev.beforeProcessSections.map((section, i) =>
        i === index ? { ...section, [field]: value } : section
      )
    }));
  };

  const updateBeforeProcessSectionImages = (index, images) => {
    setBlogData((prev) => ({
      ...prev,
      beforeProcessSections: prev.beforeProcessSections.map((section, i) =>
        i === index ? { ...section, images } : section
      )
    }));
  };

  // Functions for after process sections
  const addAfterProcessSection = () => {
    setBlogData((prev) => ({
      ...prev,
      afterProcessSections: [
        ...prev.afterProcessSections,
        {
          id: Date.now(),
          title: '',
          description: '',
          images: []
        }
      ]
    }));
  };

  const removeAfterProcessSection = (index) => {
    setBlogData((prev) => ({
      ...prev,
      afterProcessSections: prev.afterProcessSections.filter(
        (_, i) => i !== index
      )
    }));
  };

  const updateAfterProcessSection = (index, field, value) => {
    setBlogData((prev) => ({
      ...prev,
      afterProcessSections: prev.afterProcessSections.map((section, i) =>
        i === index ? { ...section, [field]: value } : section
      )
    }));
  };

  const updateAfterProcessSectionImages = (index, images) => {
    setBlogData((prev) => ({
      ...prev,
      afterProcessSections: prev.afterProcessSections.map((section, i) =>
        i === index ? { ...section, images } : section
      )
    }));
  };

  // Functions for rich content sections
  const addRichContentSection = () => {
    setBlogData((prev) => ({
      ...prev,
      richContentSections: [
        ...prev.richContentSections,
        {
          id: Date.now(),
          title: '',
          content: '',
          position: 'after-intro', // after-intro, before-process, after-process, before-cta
          styling: {
            backgroundColor: 'transparent',
            textColor: '#374151',
            fontSize: 'base',
            padding: 'normal',
            borderRadius: 'none',
            border: 'none'
          }
        }
      ]
    }));
  };

  const removeRichContentSection = (index) => {
    setBlogData((prev) => ({
      ...prev,
      richContentSections: prev.richContentSections.filter(
        (_, i) => i !== index
      )
    }));
  };

  const updateRichContentSection = (index, field, value) => {
    setBlogData((prev) => ({
      ...prev,
      richContentSections: prev.richContentSections.map((section, i) =>
        i === index ? { ...section, [field]: value } : section
      )
    }));
  };

  const updateRichContentStyling = (index, stylingField, value) => {
    setBlogData((prev) => ({
      ...prev,
      richContentSections: prev.richContentSections.map((section, i) =>
        i === index
          ? {
              ...section,
              styling: {
                ...section.styling,
                [stylingField]: value
              }
            }
          : section
      )
    }));
  };

  const moveRichContentSection = (index, direction) => {
    setBlogData((prev) => {
      const newSections = [...prev.richContentSections];
      if (direction === 'up' && index > 0) {
        [newSections[index], newSections[index - 1]] = [
          newSections[index - 1],
          newSections[index]
        ];
      } else if (direction === 'down' && index < newSections.length - 1) {
        [newSections[index], newSections[index + 1]] = [
          newSections[index + 1],
          newSections[index]
        ];
      }
      return {
        ...prev,
        richContentSections: newSections
      };
    });
  };

  const validateForm = () => {
    const errors = [];

    if (!blogData.title.trim()) errors.push('Title is required');
    if (!blogData.primaryKeyword.trim())
      errors.push('Primary keyword is required');
    if (!blogData.introParagraph.trim())
      errors.push('Intro paragraph is required');
    if (blogData.metaTitle && blogData.metaTitle.length > 60) {
      errors.push('Meta title must be 60 characters or less');
    }
    if (blogData.metaDescription && blogData.metaDescription.length > 160) {
      errors.push('Meta description must be 160 characters or less');
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      // Prepare blog data for Firebase
      const blogPost = {
        // Basic Info
        title: blogData.title.trim(),
        slug: blogData.slug.trim(),
        excerpt:
          blogData.excerpt.trim() ||
          blogData.introParagraph.substring(0, 150) + '...',
        author: blogData.author,
        tags: blogData.tags
          .split(',')
          .map((tag) => tag.trim())
          .filter((tag) => tag),

        // SEO Fields
        metaTitle: blogData.metaTitle.trim() || blogData.title,
        metaDescription: blogData.metaDescription.trim() || blogData.excerpt,
        primaryKeyword: blogData.primaryKeyword.trim(),
        secondaryKeywords: blogData.secondaryKeywords
          .split(',')
          .map((kw) => kw.trim())
          .filter((kw) => kw),
        location: blogData.location,

        // Job Details
        jobType: blogData.jobType,
        projectStartDate: blogData.projectStartDate,
        projectEndDate: blogData.projectEndDate,
        clientType: blogData.clientType,

        // Images
        featuredImage: blogData.featuredImage,
        beforeImage: blogData.beforeImage,
        afterImage: blogData.afterImage,
        gallery: blogData.gallery,

        // Content Blocks
        introParagraph: blogData.introParagraph.trim(),
        beforeSection: blogData.beforeSection.trim(),
        beforeProcessSections: blogData.beforeProcessSections.map(
          (section) => ({
            id: section.id,
            title: section.title.trim(),
            description: section.description.trim(),
            images: section.images || []
          })
        ),
        processSections: blogData.processSections.map((section) => ({
          id: section.id,
          title: section.title.trim(),
          description: section.description.trim(),
          images: section.images || []
        })),
        afterProcessSections: blogData.afterProcessSections.map((section) => ({
          id: section.id,
          title: section.title.trim(),
          description: section.description.trim(),
          images: section.images || []
        })),
        afterSection: blogData.afterSection.trim(),
        clientTestimonial: blogData.clientTestimonial.trim(),
        callToActionText: blogData.callToActionText.trim(),
        callToActionLink: blogData.callToActionLink.trim(),

        // Rich Content Sections
        richContentSections: blogData.richContentSections.map((section) => ({
          id: section.id,
          title: section.title.trim(),
          content: section.content.trim(),
          position: section.position,
          styling: section.styling
        })),

        // Additional
        faqs: blogData.faqs,
        internalLinks: blogData.internalLinks.trim(),

        // Publishing
        published: blogData.published
      };

      if (isEditing) {
        // Update existing blog
        await updateBlog(editBlog.id, blogPost);
        console.log('Blog post updated with ID:', editBlog.id);
      } else {
        // Create new blog
        const blogId = await createBlog(blogPost);
        console.log('Blog post created with ID:', blogId);
      }

      // Update sitemap if blog is published
      let sitemapUpdated = false;
      if (blogData.published) {
        try {
          await updateSitemap();
          console.log('Sitemap updated successfully');
          sitemapUpdated = true;
        } catch (error) {
          console.error('Failed to update sitemap:', error);
          // Don't fail the blog creation if sitemap update fails
        }
      }

      // Show success notification with SweetAlert
      const successMessage = isEditing
        ? blogData.published
          ? 'Blog post updated and published successfully!'
          : 'Blog post updated and saved as draft!'
        : blogData.published
        ? 'Blog post created and published successfully!'
        : 'Blog post created and saved as draft!';

      const sitemapMessage =
        blogData.published && sitemapUpdated
          ? ' Your post has been added to the sitemap for search engines.'
          : '';

      Swal.fire({
        title: 'Success!',
        text: successMessage + sitemapMessage,
        icon: 'success',
        confirmButtonText: 'Continue',
        showCancelButton: true,
        cancelButtonText: 'View Post',
        showDenyButton: isEditing && blogData.published,
        denyButtonText: 'View Published Post',
        reverseButtons: true
      }).then((result) => {
        if (result.isConfirmed) {
          // User clicked "Continue" - stay on the page
          if (isEditing) {
            // For editing, stay on the same page
            console.log('Staying on edit page');
          } else {
            // For new posts, reset the form
            console.log('Resetting form for new post');
          }
        } else if (result.isDenied && isEditing && blogData.published) {
          // User clicked "View Published Post"
          navigate(`/blog/${blogData.slug}`);
        } else if (result.dismiss === Swal.DismissReason.cancel) {
          // User clicked "View Post" or "View All Posts"
          if (isEditing && blogData.published) {
            navigate(`/blog/${blogData.slug}`);
          } else {
            navigate('/blog');
          }
        }
      });

      // Only reset form if creating new blog
      if (!isEditing) {
        setBlogData({
          title: '',
          slug: '',
          excerpt: '',
          author: currentUser?.email || '',
          tags: '',
          metaTitle: '',
          metaDescription: '',
          primaryKeyword: '',
          secondaryKeywords: '',
          location: '',
          jobType: '',
          projectStartDate: '',
          projectEndDate: '',
          clientType: 'Residential', // Default to Residential
          featuredImage: null,
          beforeImage: null,
          afterImage: null,
          gallery: [],
          introParagraph: '',
          beforeSection: '',
          beforeProcessSections: [],
          processSections: [],
          afterProcessSections: [],
          afterSection: '',
          clientTestimonial: '',
          callToActionText: '',
          callToActionLink: '',
          richContentSections: [],
          faqs: [],
          internalLinks: '',
          published: false
        });
      }
    } catch (error) {
      console.error(
        `Error ${isEditing ? 'updating' : 'creating'} blog post:`,
        error
      );

      // More specific error handling
      if (error.code === 'permission-denied') {
        setError(
          'Permission denied. Please check your Firebase security rules.'
        );
      } else if (error.code === 'unavailable') {
        setError('Firebase is temporarily unavailable. Please try again.');
      } else if (error.message.includes('transport errored')) {
        setError(
          'Network connection issue. Please check your internet connection and try again.'
        );
      } else {
        setError(
          error.message ||
            `Failed to ${
              isEditing ? 'update' : 'create'
            } blog post. Please try again.`
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Authentication is now handled by ProtectedRoute component
  // This component will only render if user is authenticated and is an admin

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {isEditing ? 'Edit Blog Post' : 'Create Blog Post'}
            </h1>
            <p className="text-gray-600 mt-1">
              {isEditing
                ? 'Update your existing blog post'
                : 'Create SEO-optimized blog content for remodeling projects'}
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={() => setShowPreview(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium">
              Preview
            </button>
            <button
              onClick={handleLogout}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium">
              Logout
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Loading State for Form Population */}
        {formLoading && (
          <div className="text-center py-12">
            <div className="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm shadow rounded-md text-white bg-blue-500 hover:bg-blue-400 transition ease-in-out duration-150 cursor-not-allowed">
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Loading blog data...
            </div>
          </div>
        )}

        {/* Blog Creation Form */}
        <form
          onSubmit={handleSubmit}
          className={`space-y-8 ${
            formLoading ? 'opacity-50 pointer-events-none' : ''
          }`}>
          {/* Basic Information Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Basic Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label
                  htmlFor="title"
                  className="block text-sm font-medium text-gray-700 mb-2">
                  Blog Title *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={blogData.title}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter blog title..."
                />
              </div>

              <div>
                <label
                  htmlFor="slug"
                  className="block text-sm font-medium text-gray-700 mb-2">
                  URL Slug
                </label>
                <input
                  type="text"
                  id="slug"
                  name="slug"
                  value={blogData.slug}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="auto-generated from title"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Auto-generated from title, but editable
                </p>
              </div>

              <div>
                <label
                  htmlFor="author"
                  className="block text-sm font-medium text-gray-700 mb-2">
                  Author
                </label>
                <div className="flex gap-2">
                  <select
                    id="author"
                    name="author"
                    value={blogData.author}
                    onChange={(e) => {
                      setBlogData((prev) => ({
                        ...prev,
                        author: e.target.value
                      }));
                      // Clear the input when selecting
                      const input = document.querySelector(
                        'input[name="authorCustom"]'
                      );
                      if (input) input.value = '';
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                    <option value="">Select an author</option>
                    {authorOptions.map((author) => (
                      <option key={author} value={author}>
                        {author}
                      </option>
                    ))}
                  </select>
                  <span className="text-gray-500 self-center">or</span>
                  <input
                    type="text"
                    name="authorCustom"
                    value={blogData.author}
                    onChange={(e) => {
                      setBlogData((prev) => ({
                        ...prev,
                        author: e.target.value
                      }));
                      // Clear the select when typing
                      const select = document.getElementById('author');
                      if (select) select.value = '';
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Type custom author name"
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <label
                  htmlFor="excerpt"
                  className="block text-sm font-medium text-gray-700 mb-2">
                  Excerpt
                </label>
                <textarea
                  id="excerpt"
                  name="excerpt"
                  value={blogData.excerpt}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Brief description of the blog post..."
                />
                <p className="mt-1 text-sm text-gray-500">
                  Leave empty to auto-generate from intro paragraph
                </p>
              </div>
            </div>
          </div>

          {/* SEO Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              SEO Optimization
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="metaTitle"
                  className="block text-sm font-medium text-gray-700 mb-2">
                  Meta Title
                  <span
                    className="ml-1 text-blue-500 cursor-help"
                    title="Example: Kitchen Remodel in Tremonton, UT | Oak & Stone Remodel - Transform your kitchen with our expert remodeling services in Tremonton, Utah. Free consultation available.">
                    ℹ️
                  </span>
                </label>
                <input
                  type="text"
                  id="metaTitle"
                  name="metaTitle"
                  value={blogData.metaTitle}
                  onChange={handleInputChange}
                  maxLength={60}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Kitchen Remodel in Tremonton, UT | Oak & Stone Remodel"
                />
                <p className="mt-1 text-sm text-gray-500">
                  {blogData.metaTitle.length}/60 characters - Include location
                  and primary keyword
                </p>
              </div>

              <div>
                <label
                  htmlFor="primaryKeyword"
                  className="block text-sm font-medium text-gray-700 mb-2">
                  Primary Keyword *
                </label>
                <input
                  type="text"
                  id="primaryKeyword"
                  name="primaryKeyword"
                  value={blogData.primaryKeyword}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., kitchen remodel Tremonton"
                />
              </div>

              <div className="md:col-span-2">
                <label
                  htmlFor="metaDescription"
                  className="block text-sm font-medium text-gray-700 mb-2">
                  Meta Description
                  <span
                    className="ml-1 text-blue-500 cursor-help"
                    title="Example: Transform your kitchen with our expert remodeling services in Tremonton, Utah. Custom cabinets, granite countertops, and professional installation. Free consultation and quote available. Call today!">
                    ℹ️
                  </span>
                </label>
                <textarea
                  id="metaDescription"
                  name="metaDescription"
                  value={blogData.metaDescription}
                  onChange={handleInputChange}
                  rows={3}
                  maxLength={160}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Transform your kitchen with our expert remodeling services in Tremonton, Utah. Custom cabinets, granite countertops, and professional installation. Free consultation available."
                />
                <p className="mt-1 text-sm text-gray-500">
                  {blogData.metaDescription.length}/160 characters - Include
                  benefits, location, and call to action
                </p>
              </div>

              <div>
                <label
                  htmlFor="secondaryKeywords"
                  className="block text-sm font-medium text-gray-700 mb-2">
                  Secondary Keywords
                </label>
                <input
                  type="text"
                  id="secondaryKeywords"
                  name="secondaryKeywords"
                  value={blogData.secondaryKeywords}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="bathroom renovation, custom cabinets (comma separated)"
                />
              </div>

              <div>
                <label
                  htmlFor="location"
                  className="block text-sm font-medium text-gray-700 mb-2">
                  Location
                </label>
                <div className="flex gap-2">
                  <select
                    id="location"
                    name="location"
                    value={blogData.location}
                    onChange={(e) => {
                      setBlogData((prev) => ({
                        ...prev,
                        location: e.target.value
                      }));
                      // Clear the input when selecting
                      const input = document.querySelector(
                        'input[name="locationCustom"]'
                      );
                      if (input) input.value = '';
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                    <option value="">Select a location</option>
                    <optgroup label="Box Elder County">
                      {utahCities.slice(0, 28).map((city) => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Cache County">
                      {utahCities.slice(28, 54).map((city) => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Weber County">
                      {utahCities.slice(54, 69).map((city) => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Davis County">
                      {utahCities.slice(69, 78).map((city) => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Rich County">
                      {utahCities.slice(78, 82).map((city) => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                  <span className="text-gray-500 self-center">or</span>
                  <input
                    type="text"
                    name="locationCustom"
                    value={blogData.location}
                    onChange={(e) => {
                      setBlogData((prev) => ({
                        ...prev,
                        location: e.target.value
                      }));
                      // Clear the select when typing
                      const select = document.getElementById('location');
                      if (select) select.value = '';
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Type custom location"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Job Details Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Job Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="jobType"
                  className="block text-sm font-medium text-gray-700 mb-2">
                  Job Type
                </label>
                <select
                  id="jobType"
                  name="jobType"
                  value={blogData.jobType}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                  <option value="">Select job type</option>
                  {jobTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="projectStartDate"
                  className="block text-sm font-medium text-gray-700 mb-2">
                  Project Start Date
                </label>
                <input
                  type="date"
                  id="projectStartDate"
                  name="projectStartDate"
                  value={blogData.projectStartDate}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label
                  htmlFor="projectEndDate"
                  className="block text-sm font-medium text-gray-700 mb-2">
                  Project End Date
                </label>
                <input
                  type="date"
                  id="projectEndDate"
                  name="projectEndDate"
                  value={blogData.projectEndDate}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label
                  htmlFor="clientType"
                  className="block text-sm font-medium text-gray-700 mb-2">
                  Client Type
                </label>
                <select
                  id="clientType"
                  name="clientType"
                  value={blogData.clientType}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                  {clientTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Media Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Media</h2>

            {/* Featured Image */}
            <div className="mb-8">
              <ImageUpload
                images={blogData.featuredImage ? [blogData.featuredImage] : []}
                onImagesChange={(images) =>
                  handleImageChange('featuredImage', images[0] || null)
                }
                maxImages={1}
                title="Featured Image"
                description="Main image for the blog post"
              />
            </div>

            {/* Before/After Images */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div>
                <ImageUpload
                  images={blogData.beforeImage ? [blogData.beforeImage] : []}
                  onImagesChange={(images) =>
                    handleImageChange('beforeImage', images[0] || null)
                  }
                  maxImages={1}
                  title="Before Image"
                  description="Image showing the space before renovation"
                />
              </div>
              <div>
                <ImageUpload
                  images={blogData.afterImage ? [blogData.afterImage] : []}
                  onImagesChange={(images) =>
                    handleImageChange('afterImage', images[0] || null)
                  }
                  maxImages={1}
                  title="After Image"
                  description="Image showing the space after renovation"
                />
              </div>
            </div>

            {/* Gallery */}
            <ImageUpload
              images={blogData.gallery}
              onImagesChange={handleGalleryChange}
              maxImages={20}
              title="Project Gallery"
              description="Additional images from the project (drag to reorder)"
            />
          </div>

          {/* Content Blocks Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Content Structure
            </h2>

            <div className="space-y-6">
              <div>
                <label
                  htmlFor="introParagraph"
                  className="block text-sm font-medium text-gray-700 mb-2">
                  Intro Paragraph *
                </label>
                <RichTextToolbar
                  value={blogData.introParagraph}
                  onChange={(value) =>
                    setBlogData((prev) => ({ ...prev, introParagraph: value }))
                  }
                  placeholder="Introduce the project and what the client wanted to achieve..."
                />
              </div>

              <div>
                <label
                  htmlFor="beforeSection"
                  className="block text-sm font-medium text-gray-700 mb-2">
                  Before Section
                </label>
                <RichTextToolbar
                  value={blogData.beforeSection}
                  onChange={(value) =>
                    setBlogData((prev) => ({ ...prev, beforeSection: value }))
                  }
                  placeholder="Describe the original state of the space and the challenges..."
                />
              </div>

              {/* Before Process Paragraph Sections */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Additional Sections (Before Process)
                  </label>
                  <button
                    type="button"
                    onClick={addBeforeProcessSection}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md text-sm font-medium">
                    + Add Section
                  </button>
                </div>

                {blogData.beforeProcessSections.length === 0 && (
                  <div className="text-center py-6 border-2 border-dashed border-gray-300 rounded-lg">
                    <svg
                      className="mx-auto h-8 w-8 text-gray-400 mb-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <p className="text-gray-500 text-sm">
                      No additional sections added yet.
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Add custom sections to provide more context before the
                      process.
                    </p>
                    <div className="mt-3 text-xs text-gray-400 space-y-1">
                      <p>
                        💡 Examples: "Project Planning", "Material Selection",
                        "Design Consultation"
                      </p>
                    </div>
                  </div>
                )}

                {blogData.beforeProcessSections.map((section, index) => (
                  <div
                    key={section.id || index}
                    className="mb-6 p-4 border border-gray-200 rounded-lg bg-blue-50">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-medium text-gray-900">
                        Additional Section {index + 1}
                      </h4>
                      <button
                        type="button"
                        onClick={() => removeBeforeProcessSection(index)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium">
                        Remove Section
                      </button>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Section Title
                        </label>
                        <input
                          type="text"
                          value={section.title}
                          onChange={(e) =>
                            updateBeforeProcessSection(
                              index,
                              'title',
                              e.target.value
                            )
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., Project Planning, Material Selection, Design Consultation"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Section Content
                        </label>
                        <RichTextToolbar
                          value={section.description}
                          onChange={(value) =>
                            updateBeforeProcessSection(
                              index,
                              'description',
                              value
                            )
                          }
                          placeholder="Write your content for this section..."
                        />
                      </div>

                      <div>
                        <ImageUpload
                          images={section.images || []}
                          onImagesChange={(images) =>
                            updateBeforeProcessSectionImages(index, images)
                          }
                          maxImages={3}
                          title="Section Images"
                          description="Add images related to this section"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Dynamic Process Sections */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Process Sections
                  </label>
                  <button
                    type="button"
                    onClick={addProcessSection}
                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-md text-sm font-medium">
                    + Add Section
                  </button>
                </div>

                {blogData.processSections.length === 0 && (
                  <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400 mb-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                      />
                    </svg>
                    <p className="text-gray-500 font-medium">
                      No process sections added yet.
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      Click "Add Section" to start documenting your project
                      process.
                    </p>
                    <div className="mt-4 text-xs text-gray-400 space-y-1">
                      <p>
                        💡 Tip: Add sections like "Demolition", "Installation",
                        "Finishing Touches"
                      </p>
                      <p>
                        📸 Include photos of each phase for better engagement
                      </p>
                    </div>
                  </div>
                )}

                {blogData.processSections.map((section, index) => (
                  <div
                    key={section.id || index}
                    className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-medium text-gray-900">
                        Process Section {index + 1}
                      </h4>
                      <button
                        type="button"
                        onClick={() => removeProcessSection(index)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium">
                        Remove Section
                      </button>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Section Title
                        </label>
                        <input
                          type="text"
                          value={section.title}
                          onChange={(e) =>
                            updateProcessSection(index, 'title', e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., Demolition Phase, Cabinet Installation, Final Touches, Painting & Finishing"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Section Description
                        </label>
                        <RichTextToolbar
                          value={section.description}
                          onChange={(value) =>
                            updateProcessSection(index, 'description', value)
                          }
                          placeholder="Describe this phase of the project in detail..."
                        />
                      </div>

                      <div>
                        <ImageUpload
                          images={section.images || []}
                          onImagesChange={(images) =>
                            updateProcessSectionImages(index, images)
                          }
                          maxImages={5}
                          title="Section Images"
                          description="Add images related to this process phase"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* After Process Paragraph Sections */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Additional Sections (After Process)
                  </label>
                  <button
                    type="button"
                    onClick={addAfterProcessSection}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded-md text-sm font-medium">
                    + Add Section
                  </button>
                </div>

                {blogData.afterProcessSections.length === 0 && (
                  <div className="text-center py-6 border-2 border-dashed border-gray-300 rounded-lg">
                    <svg
                      className="mx-auto h-8 w-8 text-gray-400 mb-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <p className="text-gray-500 text-sm">
                      No additional sections added yet.
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Add custom sections to provide more details after the
                      process.
                    </p>
                    <div className="mt-3 text-xs text-gray-400 space-y-1">
                      <p>
                        💡 Examples: "Final Results", "Client Satisfaction",
                        "Maintenance Tips"
                      </p>
                    </div>
                  </div>
                )}

                {blogData.afterProcessSections.map((section, index) => (
                  <div
                    key={section.id || index}
                    className="mb-6 p-4 border border-gray-200 rounded-lg bg-purple-50">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-medium text-gray-900">
                        Additional Section {index + 1}
                      </h4>
                      <button
                        type="button"
                        onClick={() => removeAfterProcessSection(index)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium">
                        Remove Section
                      </button>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Section Title
                        </label>
                        <input
                          type="text"
                          value={section.title}
                          onChange={(e) =>
                            updateAfterProcessSection(
                              index,
                              'title',
                              e.target.value
                            )
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., Final Results, Client Satisfaction, Maintenance Tips"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Section Content
                        </label>
                        <RichTextToolbar
                          value={section.description}
                          onChange={(value) =>
                            updateAfterProcessSection(
                              index,
                              'description',
                              value
                            )
                          }
                          placeholder="Write your content for this section..."
                        />
                      </div>

                      <div>
                        <ImageUpload
                          images={section.images || []}
                          onImagesChange={(images) =>
                            updateAfterProcessSectionImages(index, images)
                          }
                          maxImages={3}
                          title="Section Images"
                          description="Add images related to this section"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <label
                  htmlFor="afterSection"
                  className="block text-sm font-medium text-gray-700 mb-2">
                  After Section
                </label>
                <RichTextToolbar
                  value={blogData.afterSection}
                  onChange={(value) =>
                    setBlogData((prev) => ({ ...prev, afterSection: value }))
                  }
                  placeholder="Describe the final result and how it meets the client's needs..."
                />
              </div>

              <div>
                <label
                  htmlFor="clientTestimonial"
                  className="block text-sm font-medium text-gray-700 mb-2">
                  Client Testimonial
                </label>
                <RichTextToolbar
                  value={blogData.clientTestimonial}
                  onChange={(value) =>
                    setBlogData((prev) => ({
                      ...prev,
                      clientTestimonial: value
                    }))
                  }
                  placeholder="Quote from the client about their experience..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="callToActionText"
                    className="block text-sm font-medium text-gray-700 mb-2">
                    Call to Action Text
                  </label>
                  <RichTextToolbar
                    value={blogData.callToActionText}
                    onChange={(value) =>
                      setBlogData((prev) => ({
                        ...prev,
                        callToActionText: value
                      }))
                    }
                    placeholder="Encourage readers to contact you for their own project..."
                  />
                </div>
                <div>
                  <label
                    htmlFor="callToActionLink"
                    className="block text-sm font-medium text-gray-700 mb-2">
                    Call to Action Link
                  </label>
                  <input
                    type="url"
                    id="callToActionLink"
                    name="callToActionLink"
                    value={blogData.callToActionLink}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://example.com/contact"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Rich Content Sections */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Rich Content Sections
            </h2>
            <p className="text-gray-600 mb-6">
              Add custom content sections with advanced styling options. These
              sections can be positioned anywhere in your blog article and
              support HTML formatting.
            </p>

            <div className="flex items-center justify-between mb-6">
              <label className="block text-sm font-medium text-gray-700">
                Custom Content Sections
              </label>
              <button
                type="button"
                onClick={addRichContentSection}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium">
                + Add Rich Content Section
              </button>
            </div>

            {blogData.richContentSections.length === 0 && (
              <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400 mb-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <p className="text-gray-500 font-medium">
                  No rich content sections added yet.
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  Add custom content sections with advanced styling and
                  positioning options.
                </p>
                <div className="mt-4 text-xs text-gray-400 space-y-1">
                  <p>
                    💡 Perfect for: Custom layouts, highlighted content, special
                    announcements
                  </p>
                  <p>
                    🎨 Features: HTML formatting, custom colors, positioning
                    control
                  </p>
                  <p>
                    📱 Responsive: Automatically adapts to different screen
                    sizes
                  </p>
                </div>
              </div>
            )}

            {blogData.richContentSections.map((section, index) => (
              <RichTextEditor
                key={section.id || index}
                section={section}
                index={index}
                onUpdate={updateRichContentSection}
                onUpdateStyling={updateRichContentStyling}
                onRemove={removeRichContentSection}
                onMoveUp={(index) => moveRichContentSection(index, 'up')}
                onMoveDown={(index) => moveRichContentSection(index, 'down')}
              />
            ))}
          </div>

          {/* FAQ Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <FAQBlock faqs={blogData.faqs} onFaqsChange={handleFaqsChange} />
          </div>

          {/* Additional Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Additional
            </h2>
            <div className="space-y-6">
              <div>
                <label
                  htmlFor="tags"
                  className="block text-sm font-medium text-gray-700 mb-2">
                  Tags
                </label>
                <input
                  type="text"
                  id="tags"
                  name="tags"
                  value={blogData.tags}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="kitchen remodel, bathroom renovation, cabinets (comma separated)"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Separate tags with commas
                </p>
              </div>

              <div>
                <label
                  htmlFor="internalLinks"
                  className="block text-sm font-medium text-gray-700 mb-2">
                  Internal Links
                </label>
                <input
                  type="text"
                  id="internalLinks"
                  name="internalLinks"
                  value={blogData.internalLinks}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://example.com/kitchen-remodel, https://example.com/contact"
                />
                <p className="mt-1 text-sm text-gray-500">
                  URLs to link to from this post (comma separated)
                </p>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="published"
                  name="published"
                  checked={blogData.published}
                  onChange={(e) =>
                    setBlogData((prev) => ({
                      ...prev,
                      published: e.target.checked
                    }))
                  }
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="published"
                  className="ml-2 block text-sm text-gray-900">
                  Publish immediately (uncheck to save as draft)
                </label>
              </div>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-between items-center pt-6 border-t border-gray-200">
            <div className="flex space-x-4">
              {isEditing ? (
                <button
                  type="button"
                  onClick={() => navigate('/blog-management')}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium">
                  Back to Blog Management
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate('/admin')}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium">
                  Back to Dashboard
                </button>
              )}
              <button
                type="button"
                onClick={() => navigate('/')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium">
                Go to Homepage
              </button>
            </div>

            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => setShowPreview(true)}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium">
                Preview
              </button>
              <button
                type="submit"
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-6 py-2 rounded-md text-sm font-medium">
                {loading
                  ? isEditing
                    ? 'Updating...'
                    : 'Creating...'
                  : blogData.published
                  ? isEditing
                    ? 'Update & Publish'
                    : 'Publish Blog Post'
                  : isEditing
                  ? 'Update Draft'
                  : 'Save as Draft'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Blog Preview Modal */}
      <BlogPreview
        blogData={blogData}
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
      />
    </div>
  );
};

export default BlogCreation;
